import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Banknote, FileText, CreditCard, MoreHorizontal, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import CardBrandStrip from './CardBrandStrip';

/**
 * In-office payment modal — admin records a payment that happened in
 * person (cash, check, or other) OR launches the public Collect.js card
 * form in a new tab. Defaults the amount to the current balance due.
 *
 * Backend:
 *   POST /api/proposals/{id}/admin-payment (cash/check/other)
 *   GET  /pay/{id}                          (card — public Collect.js flow)
 */

const METHODS = [
  { id: 'card',  label: 'Card',  icon: CreditCard,    hint: 'Opens the secure Collect.js terminal in a new tab' },
  { id: 'cash',  label: 'Cash',  icon: Banknote,      hint: 'Record an in-office cash payment' },
  { id: 'check', label: 'Check', icon: FileText,      hint: 'Record a check (capture check #)' },
  { id: 'other', label: 'Other', icon: MoreHorizontal,hint: 'Wire transfer, store credit, etc.' },
];

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

const AdminPaymentModal = ({ proposal, onClose, onPaymentRecorded }) => {
  const balance = (() => {
    const payments = proposal?.payments || [];
    const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return Math.max((Number(proposal?.total_amount) || 0) - paid, 0);
  })();

  const [method, setMethod] = useState('card');
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : '');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setAmount(balance > 0 ? balance.toFixed(2) : '');
  }, [balance]);

  const setFull = () => setAmount(balance.toFixed(2));
  const setHalf = () => setAmount((balance / 2).toFixed(2));

  const openCardTerminal = async () => {
    const { data } = await axios.get(`/proposals/${proposal?.id}/share-link`);
    window.open(data.url, '_blank', 'noopener,noreferrer');
    toast.info('Customer payment page opened in a new tab — process the card there.');
    // Close the modal so the admin doesn't accidentally double-record a
    // payment by also submitting cash/check below. The parent re-fetches
    // the proposal automatically, so the new card transaction will appear.
    onClose?.();
    onPaymentRecorded?.({ method: 'card', via: 'collect_js_tab' });
  };

  const submitManualPayment = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Enter an amount greater than zero');
      return;
    }
    if (amt > balance + 0.01) {
      toast.error(`Amount ${fmt(amt)} exceeds balance ${fmt(balance)}`);
      return;
    }
    if (method === 'check' && !reference.trim()) {
      if (!window.confirm('No check number entered. Continue anyway?')) return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`/proposals/${proposal.id}/admin-payment`, {
        method,
        amount: amt,
        reference: reference.trim() || null,
        note: note.trim() || null,
        send_receipt: sendReceipt,
      });
      toast.success(
        data.is_fully_paid
          ? `Paid in full — ${fmt(data.amount_recorded)} recorded`
          : `Recorded ${fmt(data.amount_recorded)} · ${fmt(data.balance_due)} balance remaining`,
      );
      // Surface receipt delivery state so the admin knows whether the
      // customer actually got the confirmation email.
      if (sendReceipt) {
        if (data.receipt_email_sent === true) {
          toast.info(`Receipt emailed to ${proposal?.customer_email || 'customer'}`);
        } else if (data.receipt_email_sent === false) {
          toast.warning('Payment saved, but the receipt email did not go out (kill switch or SMTP error).');
        } else if (data.receipt_email_sent == null) {
          toast.warning('Payment saved, but no receipt sent — proposal is missing a customer email.');
        }
      }
      onPaymentRecorded?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const isCard = method === 'card';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      data-testid="admin-payment-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, background: '#fff', borderRadius: 14,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Take Payment</h2>
            <div style={{ marginTop: 2, color: '#64748b', fontSize: 12 }}>
              {proposal?.proposal_number} · {proposal?.customer_name || 'Customer'}
            </div>
          </div>
          <button onClick={onClose} data-testid="admin-payment-close" style={iconBtn} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569' }}>
              <span>Order total</span><span style={{ color: '#0f172a', fontWeight: 600 }}>{fmt(proposal?.total_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', marginTop: 4 }}>
              <span>Already paid</span>
              <span style={{ color: '#16a34a', fontWeight: 600 }}>{fmt((proposal?.total_amount || 0) - balance)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px dashed #cbd5e1' }}>
              <span style={{ fontWeight: 700, color: '#c2410c' }}>Balance due</span>
              <span style={{ fontWeight: 800, color: '#c2410c', fontSize: 18 }} data-testid="admin-payment-balance">{fmt(balance)}</span>
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Payment method
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 18 }}>
            {METHODS.map((m) => {
              const Icon = m.icon;
              const active = method === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  data-testid={`method-${m.id}`}
                  title={m.hint}
                  style={{
                    padding: '12px 8px', borderRadius: 10, cursor: 'pointer',
                    background: active ? '#0ea5e9' : '#fff',
                    color: active ? '#fff' : '#0f172a',
                    border: `1px solid ${active ? '#0ea5e9' : '#cbd5e1'}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Icon size={20} /> {m.label}
                </button>
              );
            })}
          </div>

          {isCard ? (
            <div style={{ padding: 14, borderRadius: 10, background: '#f0f9ff', border: '1px solid #bae6fd', color: '#075985', fontSize: 13, lineHeight: 1.5 }}>
              <strong>Card payments use Collect.js (PCI-scoped).</strong>
              <div style={{ marginTop: 6 }}>
                Click below to open the secure payment terminal in a new tab. Process the customer's card there — the payment will appear on this proposal automatically when done.
              </div>
              <CardBrandStrip compact />
              <Button
                onClick={openCardTerminal}
                data-testid="open-card-terminal"
                style={{ marginTop: 12, background: '#0ea5e9', color: '#fff' }}
              >
                <ExternalLink size={14} style={{ marginRight: 6 }} /> Open Card Terminal ({fmt(balance)})
              </Button>
            </div>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Amount
              </label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="number" min="0" step="0.01" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="admin-payment-amount"
                  style={{ flex: 1, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 16 }}
                />
                <button type="button" onClick={setFull} data-testid="amount-full" style={chipBtn}>Full</button>
                <button type="button" onClick={setHalf} data-testid="amount-half" style={chipBtn}>½</button>
              </div>

              {method === 'check' && (
                <input
                  type="text" placeholder="Check number" value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  data-testid="admin-payment-reference"
                  style={inputStyle}
                />
              )}
              {method === 'other' && (
                <input
                  type="text" placeholder="Reference (wire #, store credit, etc.)" value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  data-testid="admin-payment-reference"
                  style={inputStyle}
                />
              )}

              <textarea
                placeholder="Optional note (visible only to admins)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                data-testid="admin-payment-note"
                style={{ ...inputStyle, resize: 'vertical' }}
              />

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendReceipt}
                  onChange={(e) => setSendReceipt(e.target.checked)}
                  data-testid="admin-payment-send-receipt"
                />
                Email the customer a payment confirmation
              </label>

              <Button
                onClick={submitManualPayment}
                disabled={submitting}
                data-testid="admin-payment-submit"
                style={{ marginTop: 16, width: '100%', background: '#16a34a', color: '#fff', fontSize: 15, fontWeight: 700, padding: '12px' }}
              >
                {submitting ? 'Recording…' : `Record ${METHODS.find((m) => m.id === method)?.label} Payment`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: 14, marginTop: 8,
};
const chipBtn = {
  padding: '0 14px', background: '#f1f5f9', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: 13, color: '#475569', cursor: 'pointer', fontWeight: 600,
};
const iconBtn = {
  background: 'transparent', border: 'none', borderRadius: 8, padding: 6,
  cursor: 'pointer', color: '#475569',
};

export default AdminPaymentModal;
